<script setup lang="ts">
import type { UnwrapNestedRefs } from 'vue'
import { TAILOR_DEMO_MAX_POSTING } from '~/composables/useTailorDemo'
import type { PostingDialog } from '~/composables/usePostingDialog'

/**
 * "Meanwhile: which job are you applying for?" over a CV being built. The
 * state and its rules are usePostingDialog's; this is only how it looks.
 */
const props = defineProps<{
  dialog: UnwrapNestedRefs<PostingDialog>
  /** The CV is still being written: a spinner, then a tick once it is ready. */
  building: boolean
}>()

const { t } = useI18n()

function skip() {
  if (!props.dialog.answered) props.dialog.answer(null)
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="dialog.isOpen"
      class="fixed inset-0 z-[1200] flex items-end justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-4"
      @click.self="skip"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="posting-dialog-title"
        class="sofax max-h-[92svh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 shadow-lift sm:rounded-[0.625rem] sm:p-8 dark:bg-gray-800"
        @keydown.esc="skip"
      >
        <button
          v-if="!dialog.answered"
          type="button"
          class="float-right -mr-2 -mt-2 inline-flex size-9 items-center justify-center rounded-full text-ink hover:bg-surface dark:text-white dark:hover:bg-gray-700"
          :aria-label="t('common.close')"
          @click="skip"
        >
          <i class="bi bi-x-lg" />
        </button>

        <!-- The CV, being written in the meantime -->
        <div class="mb-3 flex items-center gap-2 text-sm font-semibold" aria-live="polite">
          <template v-if="building">
            <span class="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent text-accent" aria-hidden="true" />
            <span class="text-ink dark:text-white">{{ t('cvChat.posting.building') }}</span>
          </template>
          <template v-else>
            <i class="bi bi-check-circle-fill text-accent" aria-hidden="true" />
            <span class="text-accent-strong dark:text-accent-soft">{{ t('cvChat.posting.ready') }}</span>
          </template>
        </div>

        <h3 id="posting-dialog-title" class="mb-2 !text-2xl">{{ t('cvChat.posting.title') }}</h3>

        <!-- Answered while the CV is still being written: the dialog closes on its own -->
        <p v-if="dialog.answered" class="!text-base">{{ t('cvChat.posting.waiting') }}</p>

        <form v-else novalidate @submit.prevent="dialog.answer(dialog.text)">
          <p class="!mb-4 !text-base">{{ t('cvChat.posting.text') }}</p>
          <div class="rounded-[0.625rem] border border-line bg-white transition-colors focus-within:border-accent dark:border-gray-700 dark:bg-gray-800">
            <label for="posting-dialog-text" class="sr-only">{{ t('cvChat.posting.placeholder') }}</label>
            <textarea
              id="posting-dialog-text"
              :value="dialog.text"
              rows="5"
              :maxlength="TAILOR_DEMO_MAX_POSTING"
              :placeholder="t('cvChat.posting.placeholder')"
              class="block w-full resize-y rounded-[0.625rem] border-0 bg-transparent p-4 text-base leading-relaxed text-ink placeholder:text-muted focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-gray-500"
              @input="dialog.setText(($event.target as HTMLTextAreaElement).value)"
            />
          </div>
          <p v-if="dialog.error" class="mt-3 !text-sm text-amber-700 dark:text-amber-400">{{ dialog.error }}</p>
          <SofaxBtn
            type="submit"
            variant="dark"
            block
            class="mt-4"
            :disabled="!dialog.text.trim()"
            :label="t('cvChat.posting.cta')"
          />
          <p class="mt-4 text-center">
            <button type="button" class="link !text-sm" @click="skip">{{ t('cvChat.posting.skip') }}</button>
          </p>
        </form>
      </div>
    </div>
  </Teleport>
</template>
